import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { PRINCIPALE, contesto, useTraduzioni } from '../i18n';
import { messaggioErrore } from '../components/accesso/campi';
import Icona from '../components/cliente/Icone';
import Anteprima from '../components/documenti/Anteprima';
import { giornoEOra } from '../utils/documenti';
import { ilGiorno } from '../utils/percorso';
import '../components/cliente/documenti.css';

// /firma/:id — il cliente firma dal telefono un modulo mandato dallo staff (incarico, privacy…): legge il PDF,
// firma col dito, scrive nome e cognome, conferma. Il PDF firmato (con data, ora e impronta) va nei documenti.

const ETICHETTE = { nome: 'profilo.nome', cognome: 'profilo.cognome', codice_fiscale: 'profilo.codice_fiscale',
  indirizzo: 'documenti.firmare.indirizzo', data: 'documenti.firmare.data' };
// Il messaggio del server quando lo staff ha rimandato il modulo (la versione aperta non vale più): resta in
// italiano perché è proprio la frase con cui il server risponde, e la si confronta con `detail`
export const VERSIONE_NUOVA = 'Ne è arrivata una versione nuova: la trovate in Documenti';
// La regola del server (firme.righe_riepilogo): "€" solo alle cifre ("3.000 €"); "3% + IVA" resta com'è
export const importo = (valore) => {
  if (valore == null || valore === '') return null;
  return /^[\d.,]+$/.test(String(valore)) ? `${valore} €` : String(valore);
};
const erroreApertura = (e, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const codice = e?.response?.status;
  const dettaglio = e?.response?.data?.detail;
  if (codice === 404 && dettaglio === VERSIONE_NUOVA) return t('documenti.firmare.versione_nuova');
  return codice === 404 || codice === 403 ? t('documenti.firmare.non_vostro') : messaggioErrore(e, t('documenti.firmare.non_aperto'), lingua);
};
// 403/404: il documento non c'è più (annullato, sostituito, non vostro) e riprovare non serve. Il resto (rete, 5xx,
// 429) passa: si può riprovare, e con il documento già a video restano i dati di prima con un avviso
const definitivo = (e) => [403, 404].includes(e?.response?.status);

// Riquadro della firma: disegno col dito o col mouse, in alta risoluzione, sfondo trasparente.
// I tratti si tengono: se la tela cambia misura (telefono girato, finestra ridimensionata) si rifanno dimensioni e scala
// e si ridisegna la firma, rimpicciolita se serve per starci tutta. Prima la tela restava con le misure di prima: il
// tratto non seguiva più il dito e metà firma finiva fuori.
const MIN_PUNTI = 60;
const stile = (ctx) => {
  ctx.lineWidth = 2.6;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = '#141414';
};
const linea = (ctx, a, b) => {
  ctx.beginPath();
  ctx.moveTo(...a);
  ctx.lineTo(...b);
  ctx.stroke();
};
const scalaDi = (misura, rif) => (rif ? Math.min(1, misura.w / rif.w, misura.h / rif.h) : 1);
const immagineDi = (tela, punti) => (punti > MIN_PUNTI ? tela.toDataURL('image/png') : null);

const RiquadroFirma = ({ onCambia, azzera }) => {
  const { t } = useTraduzioni();
  const tela = useRef(null);
  // I punti nelle misure della tela del primo tratto (rif); a video si scalano di s = min(1, w/rif.w, h/rif.h): mai più
  // grande di com'è stata fatta, più piccola se non ci sta, e girando avanti e indietro non si rimpicciolisce due volte
  const tratti = useRef([]); // [[[x, y], …], …]
  const rif = useRef(null);
  const misura = useRef({ w: 0, h: 0 });
  const disegna = useRef(false);
  const punti = useRef(0);
  const scala = () => scalaDi(misura.current, rif.current);

  // Misure e scala della tela dalle misure a video; la firma già fatta si ridisegna (scalata se la tela è cambiata)
  const adatta = useCallback((pulisci) => {
    const c = tela.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    const w = Math.max(1, r.width);
    const h = Math.max(1, r.height);
    const prima = misura.current;
    if (pulisci) {
      tratti.current = [];
      rif.current = null;
      punti.current = 0;
    } else if (prima.w === w && prima.h === h && c.width) {
      return; // niente di cambiato
    }
    misura.current = { w, h };
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.max(1, Math.round(w * dpr));
    c.height = Math.max(1, Math.round(h * dpr));
    const ctx = c.getContext('2d');
    if (!ctx) return;
    if (ctx.setTransform) ctx.setTransform(dpr, 0, 0, dpr, 0, 0); else ctx.scale(dpr, dpr);
    stile(ctx);
    const s = scalaDi(misura.current, rif.current);
    let lunghezza = 0;
    tratti.current.forEach((t) => {
      const q = t.map(([x, y]) => [x * s, y * s]);
      q.slice(1).forEach((p, i) => { linea(ctx, q[i], p); lunghezza += Math.hypot(p[0] - q[i][0], p[1] - q[i][1]); });
    });
    if (!pulisci && tratti.current.length) {
      punti.current = lunghezza;
      onCambia(immagineDi(c, lunghezza));
    }
  }, [onCambia]);

  useEffect(() => { adatta(true); onCambia(null); }, [adatta, azzera, onCambia]);

  useEffect(() => {
    const cambia = () => adatta(false);
    window.addEventListener('resize', cambia);
    window.addEventListener('orientationchange', cambia);
    const osserva = typeof ResizeObserver === 'function' && tela.current ? new ResizeObserver(cambia) : null;
    osserva?.observe(tela.current);
    return () => {
      window.removeEventListener('resize', cambia);
      window.removeEventListener('orientationchange', cambia);
      osserva?.disconnect();
    };
  }, [adatta]);

  const posizione = (e) => {
    const r = tela.current.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  const giu = (e) => {
    e.preventDefault();
    adatta(false); // se la tela è cambiata e l'evento resize non è ancora arrivato
    tela.current.setPointerCapture?.(e.pointerId);
    disegna.current = true;
    if (!rif.current) rif.current = { ...misura.current };
    const s = scala();
    const [x, y] = posizione(e);
    tratti.current.push([[x / s, y / s]]);
  };
  const muovi = (e) => {
    if (!disegna.current) return;
    const tratto = tratti.current[tratti.current.length - 1];
    const s = scala();
    const ultimo = tratto[tratto.length - 1].map((v) => v * s); // dove si vede adesso
    const p = posizione(e);
    const ctx = tela.current.getContext('2d');
    if (ctx) linea(ctx, ultimo, p);
    punti.current += Math.hypot(p[0] - ultimo[0], p[1] - ultimo[1]);
    tratto.push([p[0] / s, p[1] / s]);
  };
  const su = () => {
    if (!disegna.current) return;
    disegna.current = false;
    onCambia(immagineDi(tela.current, punti.current));
  };
  return (
    <canvas ref={tela} className="tela-firma" aria-label={t('documenti.firmare.tela_aria')} data-testid="tela-firma"
      onPointerDown={giu} onPointerMove={muovi} onPointerUp={su} onPointerCancel={su} />
  );
};

const FirmaDocumento = () => {
  const { t, l: lingua } = useTraduzioni();
  const { id } = useParams();
  const { user } = useAuth();
  const [firma, setFirma] = useState(null);
  const [errore, setErrore] = useState(null); // { testo, riprova }: la pagina non si può mostrare
  const [nonAggiornato, setNonAggiornato] = useState(null); // rilettura non riuscita con il documento a video
  const [immagine, setImmagine] = useState(null);
  const [azzera, setAzzera] = useState(0);
  const [nome, setNome] = useState('');
  const [letto, setLetto] = useState(false);
  const [aperto, setAperto] = useState(false);
  const [attesa, setAttesa] = useState(false);
  const inVolo = useRef(false);
  const haDati = useRef(false);

  // Prima ogni errore diventava la pagina dell'errore, senza "Riprovate"; e una firma riuscita seguita da una
  // rilettura a 503 mostrava l'errore insieme all'avviso "Firmato". Ora: 403/404 → la pagina dell'errore (come
  // prima); rete o server giù → con il documento a video restano i dati con l'avviso "non aggiornato" e "Riprovate",
  // alla prima apertura l'errore con "Riprovate".
  const carica = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/firme/${id}`);
      haDati.current = true;
      setFirma(r.data);
      setErrore(null);
      setNonAggiornato(null);
    } catch (e) {
      if (haDati.current && !definitivo(e)) setNonAggiornato(messaggioErrore(e, t('documenti.firmare.non_riletto'), lingua));
      else setErrore({ testo: erroreApertura(e, lingua), riprova: !definitivo(e) });
    }
  }, [id, t, lingua]);
  const riprova = () => { setErrore(null); carica(); };
  useEffect(() => { carica(); }, [carica]);
  // il titolo della scheda del browser (prima restava "MK Client")
  useEffect(() => {
    const precedente = document.title;
    document.title = `MK Client — ${t('documenti.firma')}`;
    return () => { document.title = precedente; };
  }, [t]);
  useEffect(() => { if (user && !nome) setNome([user.nome, user.cognome].filter(Boolean).join(' ')); }, [user]); // eslint-disable-line
  const cambiaFirma = useCallback((v) => setImmagine(v), []);
  const chiudiAnteprima = useCallback(() => setAperto(false), []);

  const firmaOra = async () => {
    if (inVolo.current) return; // doppio tocco: una firma sola (il server comunque ne accetta una)
    inVolo.current = true;
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/firme/${id}/firma`, { firma: immagine, nome: nome.trim(), accetto: letto });
      toast.success(t('documenti.firmare.fatto_toast'));
      // il server risponde con il documento firmato (firme.py): la pagina passa a "Firmato" senza aspettare la
      // rilettura; senza (server di prima) si segna firmato qui e si rilegge
      if (r?.data?.firma?.stato === 'firmata') {
        setFirma(r.data.firma);
        setNonAggiornato(null);
      } else {
        setFirma((f) => ({ ...f, stato: 'firmata', firmata: { nome: nome.trim(), data: new Date().toISOString() } }));
        carica();
      }
    } catch (e) {
      const codice = e?.response?.status;
      // 404: lo staff ha annullato o rimandato il modulo mentre la pagina era aperta (prima restava firmabile e
      // rispondeva sempre 404): si rilegge e la pagina lo dice ("Ne è arrivata una versione nuova…")
      if (codice === 404) carica();
      else {
        toast.error(messaggioErrore(e, t('documenti.firmare.non_riuscita'), lingua));
        if (codice === 409) carica(); // già firmato (o firma in corso da un'altra scheda): si rilegge
      }
    } finally {
      inVolo.current = false;
      setAttesa(false);
    }
  };

  if (errore) {
    return (
      <main className="mk-home pagina-documenti wrap" data-testid="firma-errore">
        <Link className="back" to="/documenti"><Icona nome="indietro" />{t('documenti.titolo')}</Link>
        <p className="err" role="alert">{errore.testo}</p>
        {errore.testo === t('documenti.firmare.versione_nuova') && <Link className="btn sm p" to="/documenti">{t('documenti.firmare.vai_documenti')}</Link>}
        {errore.riprova && <button type="button" className="btn sm" onClick={riprova}>{t('comune.riprova')}</button>}
      </main>
    );
  }
  if (!firma) return <main className="mk-home pagina-documenti wrap"><div className="attesa"><span>{t('documenti.firmare.apro')}</span></div></main>;
  const firmata = firma.stato === 'firmata';
  // nell'ora di Roma, come nel PDF firmato ("16:10 ora italiana"): prima l'ora del telefono (10:10 a New York)
  const quando = firmata && firma.firmata?.data ? giornoEOra(firma.firmata.data, undefined, lingua) : '';
  const righe = [...Object.entries(ETICHETTE).map(([k, n]) => [t(n), firma.valori?.[k]]),
    ...(firma.importi || []).map((i) => [i.etichetta, importo(firma.valori?.[i.chiave])])].filter(([, v]) => v);

  return (
    <main className="mk-home pagina-documenti pagina-firma wrap" data-testid="firma-documento">
      <Link className="back" to="/documenti"><Icona nome="indietro" />{t('documenti.titolo')}</Link>
      {nonAggiornato && (
        <p className="avviso-doc" role="status" data-testid="firma-non-aggiornata">
          <span>{t('documenti.firmare.non_aggiornata', { motivo: nonAggiornato.replace(/\.$/, '') })}</span>
          <button type="button" className="btn sm" onClick={carica}>{t('comune.riprova')}</button>
        </p>
      )}
      <div className="testa-doc r">
        <div>
          <div className="k">{t(firmata ? 'documenti.firmare.occhiello_fatto' : 'documenti.firmare.occhiello_da')}</div>
          <h1>{firma.nome}</h1>
          <p>{firmata
            ? (quando
              ? t('documenti.firmare.fatto_testo', { quando: lingua === PRINCIPALE ? ilGiorno(quando) : quando })
              : t('documenti.firmare.fatto_testo_senza'))
            : t('documenti.firmare.istruzioni')}</p>
        </div>
      </div>

      <section className="card r">
        <div className="ttl"><div className="i"><Icona nome="documento" /></div>
          <h2>{t(firmata ? 'documenti.firmare.documento_firmato' : 'documenti.firmare.passo1')}</h2>
          <a href="#apri" onClick={(e) => { e.preventDefault(); setAperto(true); }}>{t('documenti.firmare.apri')}</a></div>
        <div className="dati-firma">
          {righe.map(([n, v]) => <div key={n}><span>{n}</span><b>{v}</b></div>)}
        </div>
        {firma.messaggio && <p className="testo-muto" style={{ marginTop: '.8rem' }}>{firma.messaggio}</p>}
      </section>

      {!firmata && (
        <section className="card hero r" data-testid="firma-qui">
          <div className="ttl"><div className="i w"><Icona nome="fatto" /></div><h2>{t('documenti.firmare.passo2')}</h2>
            <a href="#rifai" onClick={(e) => { e.preventDefault(); setAzzera((n) => n + 1); }}>{t('documenti.firmare.cancella')}</a></div>
          <RiquadroFirma onCambia={cambiaFirma} azzera={azzera} />
          <label className="campo-firma">{t('documenti.firmare.nome_cognome')}
            <input value={nome} onChange={(e) => setNome(e.target.value)} autoComplete="name" />
          </label>
          <label className="spunta-firma">
            <input type="checkbox" checked={letto} onChange={(e) => setLetto(e.target.checked)} />
            {t('documenti.firmare.spunta')}
          </label>
          <button type="button" className={`btn p${attesa ? ' busy' : ''}`} disabled={!immagine || !letto || nome.trim().length < 3 || attesa} onClick={firmaOra}>
            {t('documenti.firmare.bottone')}
          </button>
          {!immagine && <p className="testo-muto" style={{ marginTop: '.6rem' }}>{t('documenti.firmare.nel_riquadro')}</p>}
        </section>
      )}

      {aperto && (
        // il nome del file lo dà il server, lo stesso dei documenti ("incarico-di-mediazione-firmato-oleksandr-….pdf")
        <Anteprima doc={{ id, tipo_nome: firma.nome, nome_file: firma.nome_file || `${firma.nome}.pdf`, content_type: 'application/pdf' }} onChiudi={chiudiAnteprima}
          onNonDisponibile={() => { setAperto(false); carica(); }}
          leggi={() => axios.get(`${API}/firme/${id}/pdf`, { responseType: 'blob' }).then((r) => r.data)} />
      )}
    </main>
  );
};

export default FirmaDocumento;
